import type { World } from '../sim/World';

/* ═══════════════════════════════════════════════════════════════════════════
   LA VIE DU BOSS, telle que le HUD doit la montrer.

   ⚠️ Ce module vit dans src/game/ et non dans src/sim/ : c'est une LECTURE de
   l'état du monde pour la présentation, pas une mécanique. La simulation n'a
   aucune raison de savoir qu'une barre existe.

   ── Pourquoi une barre, et pourquoi celle-ci ──
   Retour de jeu : « je ne vois pas de barre de boss, et je ne sais pas si j'en
   ai déjà rencontré ». La seconde moitié de la phrase est la plus grave — sans
   repère, un boss ressemble à un gros ennemi, et l'événement que deux minutes
   de niveau préparent ne se distingue pas de ce qui le précède.

   La barre montre DEUX choses, et c'est délibéré :

     — la vie du NOYAU, qui ne bouge pas tant que les parties tiennent. Une
       barre figée n'est pas un bug d'affichage, c'est l'information : elle
       apprend le blindage par les parties sans qu'aucun texte ne l'explique.
     — le nombre de parties encore debout, en segments. Elles descendent, elles,
       et c'est là que le joueur voit son travail avancer.

   Une barre unique agrégeant tout aurait été plus douce à regarder et aurait
   effacé la seule chose que ce combat enseigne.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface BossHealth {
  /** Vie du noyau, dans [0, 1]. */
  readonly core: number;
  /** Parties encore vivantes, et total au départ du combat. */
  readonly parts: number;
  readonly partsMax: number;
  /** Le noyau est-il intouchable tant que ses parties tiennent ? */
  readonly shielded: boolean;
}

/**
 * État du boss en scène, ou `null` s'il n'y en a pas.
 *
 * ⚠️ Le boss est retrouvé par son `uid`, jamais par son slot. Les slots sont
 * recyclés dès qu'une entité meurt : conserver un slot ferait pointer la barre
 * sur un ennemi ordinaire quelques secondes après la mort du boss, avec une
 * vie qui remonterait toute seule.
 */
export function bossHealth(w: World): BossHealth | null {
  const uid = w.run.bossUid;
  if (!w.run.bossSpawned || uid <= 0) return null;

  const en = w.enemies;
  for (let i = 0; i < en.count; i++) {
    const s = en.at(i);
    if (en.uid[s] !== uid) continue;
    const max = en.maxHp[s]!;
    const def = w.defs.enemies[en.kind[s]!];
    return {
      core: max > 0 ? Math.max(0, Math.min(1, en.hp[s]! / max)) : 0,
      parts: en.livingParts(s),
      partsMax: def?.parts?.length ?? 0,
      shielded: en.shielded[s] === 1,
    };
  }
  // Le porteur n'est plus dans le pool : le boss est mort, la barre disparaît.
  return null;
}
